import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Search, Filter, X, MapPin, Phone, Mail, Globe, GraduationCap,
  DollarSign, Clock, Building2, ChevronDown, ChevronUp, CheckCircle2,
  BookOpen, Shield, Percent, Calendar, ChevronLeft, ChevronRight, Landmark,
  Calculator, TrendingUp, Info
} from 'lucide-react';
import { getCanadaColleges, getCanadaProvinces } from '@/services/api';

const PROVINCES = [
  "Ontario", "British Columbia", "Quebec", "Alberta", "Manitoba",
  "Saskatchewan", "Nova Scotia", "New Brunswick", "Prince Edward Island",
  "Newfoundland and Labrador"
];

const PROVINCE_AID_INFO = {
  "Ontario": { program: "OSAP", grant_range: [30, 60], loan_range: [40, 70], interest: 3.5, interest_free: false },
  "British Columbia": { program: "StudentAidBC", grant_range: [25, 50], loan_range: [50, 75], interest: 0, interest_free: true },
  "Quebec": { program: "AFE", grant_range: [20, 45], loan_range: [55, 80], interest: 2.5, interest_free: false },
  "Alberta": { program: "Alberta Student Aid", grant_range: [25, 50], loan_range: [50, 75], interest: 4.0, interest_free: false },
  "Manitoba": { program: "Manitoba Student Aid", grant_range: [30, 55], loan_range: [45, 70], interest: 2.5, interest_free: false },
  "Saskatchewan": { program: "Saskatchewan Student Aid", grant_range: [25, 45], loan_range: [55, 75], interest: 3.5, interest_free: false },
  "Nova Scotia": { program: "NS Student Assistance", grant_range: [30, 55], loan_range: [45, 70], interest: 0, interest_free: true },
  "New Brunswick": { program: "StudentAid NB", grant_range: [30, 50], loan_range: [50, 70], interest: 2.5, interest_free: false },
  "Prince Edward Island": { program: "PEI Student Aid", grant_range: [30, 55], loan_range: [45, 70], interest: 0, interest_free: true },
  "Newfoundland and Labrador": { program: "StudentAidNL", grant_range: [35, 60], loan_range: [40, 65], interest: 0, interest_free: true },
};

const monthName = (m) => ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m] || '';

// OSAP-style Student Aid Estimator Calculator
const StudentAidEstimator = () => {
  const [province, setProvince] = useState('');
  const [tuition, setTuition] = useState('');
  const [living, setLiving] = useState('away_from_home');
  const [result, setResult] = useState(null);

  const calculate = () => {
    if (!province || !tuition) {
      toast.error('Select province and enter tuition amount');
      return;
    }
    const aid = PROVINCE_AID_INFO[province];
    if (!aid) return;

    const tuitionNum = parseFloat(tuition);
    const livingCost = living === 'at_home' ? 4000 : living === 'away_from_home' ? 12000 : 8000;
    const totalNeed = tuitionNum + livingCost + 2000; // books/supplies

    const avgGrantPct = (aid.grant_range[0] + aid.grant_range[1]) / 2 / 100;
    const avgLoanPct = (aid.loan_range[0] + aid.loan_range[1]) / 2 / 100;

    const estimatedGrant = Math.round(totalNeed * avgGrantPct);
    const estimatedLoan = Math.round(totalNeed * avgLoanPct);
    const totalAid = estimatedGrant + estimatedLoan;
    const outOfPocket = Math.max(0, totalNeed - totalAid);

    setResult({
      province, program: aid.program,
      totalNeed, estimatedGrant, estimatedLoan, totalAid, outOfPocket,
      interest: aid.interest, interestFree: aid.interest_free,
      grantRange: aid.grant_range, loanRange: aid.loan_range,
      livingCost, tuition: tuitionNum
    });
  };

  return (
    <Card className="bg-gradient-to-br from-[#141029] to-[#1A1040] border-[#2D2654] overflow-hidden" data-testid="aid-estimator">
      <CardContent className="p-0">
        <div className="bg-gradient-to-r from-[#F59E0B]/10 to-[#00D4FF]/10 p-5 border-b border-[#2D2654]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/20 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-[#F59E0B]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Student Aid Estimator</h2>
              <p className="text-xs text-[#6B678A]">Estimate grants vs loans for any Canadian province (OSAP, StudentAidBC, etc.)</p>
            </div>
          </div>
        </div>

        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Select value={province} onValueChange={setProvince}>
              <SelectTrigger className="bg-[#0D0B1E] border-[#2D2654] text-white" data-testid="est-province">
                <SelectValue placeholder="Province" />
              </SelectTrigger>
              <SelectContent className="bg-[#141029] border-[#2D2654] max-h-60">
                {PROVINCES.map(p => <SelectItem key={p} value={p} className="text-white">{p}</SelectItem>)}
              </SelectContent>
            </Select>

            <Input
              type="number"
              placeholder="Annual Tuition (CAD)"
              value={tuition}
              onChange={e => setTuition(e.target.value)}
              className="bg-[#0D0B1E] border-[#2D2654] text-white"
              data-testid="est-tuition"
            />

            <Select value={living} onValueChange={setLiving}>
              <SelectTrigger className="bg-[#0D0B1E] border-[#2D2654] text-white" data-testid="est-living">
                <SelectValue placeholder="Living Situation" />
              </SelectTrigger>
              <SelectContent className="bg-[#141029] border-[#2D2654]">
                <SelectItem value="at_home" className="text-white">Living at Home</SelectItem>
                <SelectItem value="away_from_home" className="text-white">Away from Home</SelectItem>
                <SelectItem value="with_dependents" className="text-white">With Dependents</SelectItem>
              </SelectContent>
            </Select>

            <Button onClick={calculate} className="bg-[#F59E0B] text-[#0F172A] hover:bg-[#D97706] font-medium" data-testid="est-calculate-btn">
              <Calculator className="w-4 h-4 mr-2" /> Calculate
            </Button>
          </div>

          {result && (
            <div className="mt-5 pt-5 border-t border-[#2D2654]" data-testid="est-result">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-4 h-4 text-[#F59E0B]" />
                <span className="text-sm font-medium text-[#F59E0B]">{result.program}</span>
                <span className="text-xs text-[#6B678A]">- {result.province}</span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-[#0D0B1E] rounded-lg p-3 text-center">
                  <p className="text-xs text-[#6B678A] mb-1">Total Need</p>
                  <p className="text-lg font-bold text-white">${result.totalNeed.toLocaleString()}</p>
                  <p className="text-xs text-[#6B678A] mt-1">Tuition + Living + Books</p>
                </div>
                <div className="bg-[#10B981]/10 rounded-lg p-3 text-center border border-[#10B981]/20">
                  <p className="text-xs text-[#10B981] mb-1">Est. Grant</p>
                  <p className="text-lg font-bold text-[#10B981]">${result.estimatedGrant.toLocaleString()}</p>
                  <p className="text-xs text-[#6B678A] mt-1">Non-repayable ({result.grantRange[0]}-{result.grantRange[1]}%)</p>
                </div>
                <div className="bg-[#8B5CF6]/10 rounded-lg p-3 text-center border border-[#8B5CF6]/20">
                  <p className="text-xs text-[#8B5CF6] mb-1">Est. Loan</p>
                  <p className="text-lg font-bold text-[#8B5CF6]">${result.estimatedLoan.toLocaleString()}</p>
                  <p className="text-xs text-[#6B678A] mt-1">
                    {result.interestFree ? 'Interest-Free' : `${result.interest}% interest`}
                  </p>
                </div>
                <div className="bg-[#00D4FF]/10 rounded-lg p-3 text-center border border-[#00D4FF]/20">
                  <p className="text-xs text-[#00D4FF] mb-1">Total Aid</p>
                  <p className="text-lg font-bold text-[#00D4FF]">${result.totalAid.toLocaleString()}</p>
                  <p className="text-xs text-[#6B678A] mt-1">Grant + Loan</p>
                </div>
                <div className="bg-[#F59E0B]/10 rounded-lg p-3 text-center border border-[#F59E0B]/20">
                  <p className="text-xs text-[#F59E0B] mb-1">Out-of-Pocket</p>
                  <p className="text-lg font-bold text-[#F59E0B]">${result.outOfPocket.toLocaleString()}</p>
                  <p className="text-xs text-[#6B678A] mt-1">Remaining cost</p>
                </div>
              </div>

              <div className="mt-3 flex items-start gap-2 bg-[#0D0B1E] rounded-lg p-3">
                <Info className="w-4 h-4 text-[#6B678A] shrink-0 mt-0.5" />
                <p className="text-xs text-[#6B678A] leading-relaxed">
                  This is an estimate based on average grant/loan percentages for {result.province}. Actual amounts depend on family income, assets, and enrollment status. Repayment starts 6 months after graduation.
                  {result.interestFree && ' Provincial loans in this province are interest-free.'}
                </p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const CanadaColleges = () => {
  const [colleges, setColleges] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 30;
  const [filters, setFilters] = useState({
    province: '', institution_type: '', student_aid: '', course_search: ''
  });

  const loadColleges = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (filters.province) params.province = filters.province;
      if (filters.institution_type) params.institution_type = filters.institution_type;
      if (filters.student_aid === 'true') params.student_aid = true;
      if (searchText.trim()) params.search = searchText.trim();
      const data = await getCanadaColleges(params);
      setColleges(data.colleges || data);
      setTotalCount(data.total_count || (data.colleges || data).length);
      setTotalPages(data.total_pages || 1);
    } catch (err) { toast.error('Failed to load colleges'); }
    finally { setLoading(false); }
  }, [page, filters, searchText]);

  const fetchProvinces = useCallback(() => {
    getCanadaProvinces().then(setProvinces).catch(() => {});
  }, []);

  useEffect(() => { fetchProvinces(); }, [fetchProvinces]);
  useEffect(() => { loadColleges(); }, [loadColleges]);

  const handleSearch = () => { setPage(0); loadColleges(); };
  const clearFilters = () => {
    setFilters({ province: '', institution_type: '', student_aid: '', course_search: '' });
    setSearchText('');
    setPage(0);
    setTimeout(loadColleges, 0);
  };

  const activeFilterCount = Object.values(filters).filter(v => v).length + (searchText ? 1 : 0);
  const totalAid = provinces.reduce((s, p) => s + (p.student_aid_colleges || 0), 0);
  const totalCollegesCount = provinces.reduce((s, p) => s + p.total_colleges, 0);

  const getFilteredCourses = (college) => {
    let courses = college.courses || [];
    if (filters.course_search) {
      const q = filters.course_search.toLowerCase();
      courses = courses.filter(c => c.name.toLowerCase().includes(q) || (c.field_of_study || '').toLowerCase().includes(q));
    }
    return courses;
  };

  return (
    <div className="space-y-5" data-testid="canada-colleges-page">
      <div>
        <h1 className="text-2xl font-medium text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
          All Colleges in Canada
        </h1>
        <p className="text-[#6B678A] text-sm mt-1">
          Complete directory of {totalCollegesCount} Canadian institutions — public & private, with student aid details
        </p>
      </div>

      {/* Student Aid Estimator Calculator */}
      <StudentAidEstimator />

      {/* Province Overview Cards */}
      {provinces.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="province-stats">
          {provinces.slice(0, 5).map(p => (
            <Card
              key={p.province}
              className={`bg-[#141029] border-[#2D2654] cursor-pointer transition-all hover:border-[#00D4FF]/40 ${filters.province === p.province ? 'border-[#00D4FF] bg-[#00D4FF]/5' : ''}`}
              onClick={() => { setFilters({...filters, province: filters.province === p.province ? '' : p.province}); setPage(0); setTimeout(loadColleges, 0); }}
            >
              <CardContent className="p-3 text-center">
                <p className="text-xs text-[#6B678A] truncate">{p.province}</p>
                <p className="text-xl font-bold text-white mt-1">{p.total_colleges}</p>
                <div className="flex justify-center gap-2 mt-1 text-xs">
                  <span className="text-[#10B981]">{p.public} pub</span>
                  <span className="text-[#8B5CF6]">{p.private} pvt</span>
                </div>
                {p.avg_grant_percentage > 0 && (
                  <p className="text-xs text-[#F59E0B] mt-1">~{p.avg_grant_percentage}% grant</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Search & Filters */}
      <Card className="bg-[#141029] border-[#2D2654]">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B678A]" />
              <Input
                placeholder="Search college name or city..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                className="bg-[#0D0B1E] border-[#2D2654] text-white pl-10"
                data-testid="college-search"
              />
            </div>
            <Button onClick={() => setShowFilters(!showFilters)} variant="outline" className="border-[#2D2654] text-[#B8B4CE] hover:bg-[#1E1A3A]">
              <Filter className="w-4 h-4 mr-2" /> Filters {activeFilterCount > 0 && <Badge className="ml-1 bg-[#00D4FF] text-[#0F172A] text-xs px-1.5">{activeFilterCount}</Badge>}
            </Button>
            <Button onClick={handleSearch} className="bg-[#00D4FF] text-[#0F172A] hover:bg-[#00B8D4]" data-testid="search-btn">
              <Search className="w-4 h-4 mr-2" /> Search
            </Button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-[#2D2654]">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Select value={filters.province} onValueChange={v => { setFilters({...filters, province: v}); setPage(0); setTimeout(loadColleges, 0); }}>
                  <SelectTrigger className="bg-[#0D0B1E] border-[#2D2654] text-white" data-testid="province-filter">
                    <SelectValue placeholder="Province" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141029] border-[#2D2654] max-h-60">
                    {PROVINCES.map(p => <SelectItem key={p} value={p} className="text-white">{p}</SelectItem>)}
                  </SelectContent>
                </Select>

                <Select value={filters.institution_type} onValueChange={v => { setFilters({...filters, institution_type: v}); setPage(0); setTimeout(loadColleges, 0); }}>
                  <SelectTrigger className="bg-[#0D0B1E] border-[#2D2654] text-white" data-testid="type-filter">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141029] border-[#2D2654]">
                    <SelectItem value="public" className="text-white">Public</SelectItem>
                    <SelectItem value="private" className="text-white">Private</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filters.student_aid} onValueChange={v => { setFilters({...filters, student_aid: v}); setPage(0); setTimeout(loadColleges, 0); }}>
                  <SelectTrigger className="bg-[#0D0B1E] border-[#2D2654] text-white" data-testid="aid-filter">
                    <SelectValue placeholder="Student Aid" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141029] border-[#2D2654]">
                    <SelectItem value="true" className="text-white">Student Aid Available</SelectItem>
                  </SelectContent>
                </Select>

                <Input
                  placeholder="Search courses..."
                  value={filters.course_search}
                  onChange={e => setFilters({...filters, course_search: e.target.value})}
                  className="bg-[#0D0B1E] border-[#2D2654] text-white"
                  data-testid="course-search-filter"
                />
              </div>
              <div className="flex justify-end mt-3">
                <Button onClick={clearFilters} variant="ghost" className="text-[#6B678A] hover:text-white text-sm">
                  <X className="w-3 h-3 mr-1" /> Clear All
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      {!loading && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#6B678A]">
            Showing <span className="text-white font-medium">{colleges.length}</span> of <span className="text-white font-medium">{totalCount}</span> colleges
            {filters.province && <span> in <span className="text-[#00D4FF]">{filters.province}</span></span>}
            <span> — Page <span className="text-white font-medium">{page + 1}</span> of <span className="text-white font-medium">{totalPages}</span></span>
          </p>
          <div className="flex gap-3 text-xs text-[#6B678A]">
            <span className="flex items-center gap-1"><Shield className="w-3 h-3 text-[#10B981]" /> {totalAid} with Student Aid</span>
          </div>
        </div>
      )}

      {/* College Cards */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Card key={`skeleton-${i}`} className="bg-[#141029] border-[#2D2654] animate-pulse"><CardContent className="p-5 h-24" /></Card>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {colleges.map((college, idx) => {
            const isExpanded = expandedId === college.id;
            const filteredCourses = getFilteredCourses(college);
            const shortCourses = filteredCourses.filter(c => c.duration_months <= 12);
            const longCourses = filteredCourses.filter(c => c.duration_months > 12);

            return (
              <Card key={college.id} className="bg-[#141029] border-[#2D2654] hover:border-[#2D2654]/80 transition-all" data-testid={`college-card-${idx}`}>
                <CardContent className="p-0">
                  <div
                    className="p-5 cursor-pointer flex items-start gap-4"
                    onClick={() => setExpandedId(isExpanded ? null : college.id)}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${college.institution_type === 'public' ? 'bg-[#10B981]/15 text-[#10B981]' : 'bg-[#8B5CF6]/15 text-[#8B5CF6]'}`}>
                      <Landmark className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-medium text-white text-base">{college.name}</h3>
                          <div className="flex items-center gap-3 mt-1 text-sm text-[#6B678A]">
                            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {college.city}, {college.province || 'ON'}</span>
                            <Badge className={`text-xs ${college.institution_type === 'public' ? 'bg-[#10B981]/15 text-[#10B981]' : 'bg-[#8B5CF6]/15 text-[#8B5CF6]'}`}>
                              {college.institution_type}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {college.student_aid && (
                            <Badge className="bg-[#F59E0B]/15 text-[#F59E0B] text-xs" data-testid="aid-badge">
                              <Shield className="w-3 h-3 mr-1" /> Student Aid
                            </Badge>
                          )}
                          <div className="text-right">
                            <p className="text-sm text-white font-medium">{college.course_count} programs</p>
                            <p className="text-xs text-[#6B678A]">{college.short_courses} short / {college.long_courses} long</p>
                          </div>
                          {isExpanded ? <ChevronUp className="w-5 h-5 text-[#6B678A]" /> : <ChevronDown className="w-5 h-5 text-[#6B678A]" />}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-4 mt-2 text-xs text-[#6B678A]">
                        {college.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {college.phone}</span>}
                        {college.partnership_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {college.partnership_email}</span>}
                        {college.website && <a href={college.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#00D4FF] hover:underline" onClick={e => e.stopPropagation()}><Globe className="w-3 h-3" /> Website</a>}
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-[#2D2654] p-5 space-y-5">
                      {college.student_aid && (
                        <div className="bg-[#0D0B1E] rounded-xl p-4" data-testid="aid-details">
                          <h4 className="text-sm font-medium text-[#F59E0B] mb-3 flex items-center gap-2"><Shield className="w-4 h-4" /> Student Aid Details</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-[#6B678A] text-xs">Grant</p>
                              <p className="text-white font-medium flex items-center gap-1"><Percent className="w-3 h-3 text-[#10B981]" /> {college.grant_percentage}%</p>
                            </div>
                            <div>
                              <p className="text-[#6B678A] text-xs">Loan</p>
                              <p className="text-white font-medium flex items-center gap-1"><Percent className="w-3 h-3 text-[#8B5CF6]" /> {college.loan_percentage}%</p>
                            </div>
                            <div>
                              <p className="text-[#6B678A] text-xs">Interest Rate</p>
                              <p className="text-white font-medium">{college.interest_rate === 0 ? <span className="text-[#10B981]">Interest-Free</span> : `${college.interest_rate}%`}</p>
                            </div>
                            <div>
                              <p className="text-[#6B678A] text-xs">Repayment Starts</p>
                              <p className="text-white font-medium text-xs">{college.repayment_start}</p>
                            </div>
                          </div>
                          {college.aid_details && <p className="text-xs text-[#6B678A] mt-3 leading-relaxed">{college.aid_details}</p>}
                        </div>
                      )}

                      {shortCourses.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-[#00D4FF] mb-3 flex items-center gap-2">
                            <Clock className="w-4 h-4" /> Short Courses ({shortCourses.length})
                            <span className="text-xs text-[#6B678A] font-normal">3-12 months</span>
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {shortCourses.map((c, i) => (
                              <div key={`short-${c.name}-${i}`} className="bg-[#0D0B1E] rounded-lg p-3 flex items-center justify-between text-sm">
                                <div className="flex-1 mr-3">
                                  <p className="text-white text-sm">{c.name}</p>
                                  <div className="flex gap-3 mt-1 text-xs text-[#6B678A]">
                                    <span>{c.duration_months}mo</span>
                                    <span className="capitalize">{c.field_of_study}</span>
                                    {c.intake_months && <span>{c.intake_months.map(m => monthName(m)).join(', ')}</span>}
                                  </div>
                                </div>
                                <span className="text-[#10B981] font-medium text-sm whitespace-nowrap">CAD {c.tuition_fee?.toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {longCourses.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-[#8B5CF6] mb-3 flex items-center gap-2">
                            <GraduationCap className="w-4 h-4" /> Long Programs ({longCourses.length})
                            <span className="text-xs text-[#6B678A] font-normal">1-4 years</span>
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {longCourses.map((c, i) => (
                              <div key={`long-${c.name}-${i}`} className="bg-[#0D0B1E] rounded-lg p-3 flex items-center justify-between text-sm">
                                <div className="flex-1 mr-3">
                                  <p className="text-white text-sm">{c.name}</p>
                                  <div className="flex gap-3 mt-1 text-xs text-[#6B678A]">
                                    <span>{c.duration_months}mo</span>
                                    <span className="capitalize">{c.degree_level}</span>
                                    <span className="capitalize">{c.field_of_study}</span>
                                    {c.intake_months && <span>{c.intake_months.map(m => monthName(m)).join(', ')}</span>}
                                  </div>
                                </div>
                                <span className="text-[#10B981] font-medium text-sm whitespace-nowrap">CAD {c.tuition_fee?.toLocaleString()}/yr</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {filteredCourses.length === 0 && (
                        <p className="text-sm text-[#6B678A] text-center py-4">No courses match your filter</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {!loading && colleges.length > 0 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="border-[#2D2654] text-[#B8B4CE]" data-testid="prev-page">
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          <span className="text-sm text-[#6B678A]">Page <span className="text-white font-medium">{page + 1}</span> of <span className="text-white font-medium">{totalPages}</span></span>
          <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages} className="border-[#2D2654] text-[#B8B4CE]" data-testid="next-page">
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}

      {colleges.length === 0 && !loading && (
        <Card className="bg-[#141029] border-[#2D2654]">
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 text-[#6B678A] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-white font-medium mb-1">No colleges found</p>
            <p className="text-[#6B678A] text-sm">Try adjusting your filters</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CanadaColleges;
